// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.
//
// Shared enrichment for one AEM `alarm_event_state` row -> the UI `Alarm` model.
//
// Alarms and findings/anomalies are the same table, the same columns and the
// same `AlarmRecord` wire shape upstream — only `event_type` differs — so both
// detail pages need the identical two-step enrichment: resolve the row's alarm
// rule (for severity/category/title), then fetch every SOP the rule or the
// manual map binds to it. This lives here rather than in alarmMappers.ts because
// it performs I/O; alarmMappers stays pure.

import type { Alarm, ResolvedSop } from '../types/alarm';
import type { AlarmApiRecord, AlarmRule } from '../types/alarmApi';
import { getArtifact } from './sopFactoryService';
import { ApiError } from './api';
import { resolveAlarmRule } from './alarmRuleService';
import {
  alarmApiRecordToAlarm,
  artifactToResolvedSop,
  priorityToSeverity,
  resolveSopIds,
} from './alarmMappers';

/**
 * Look up the rule for a record. There is no FK from `aem.alarm_event_state` to
 * the rule registry — `rule_id` matches `alarm_code` by convention only — so a
 * missing rule is normal and resolves to null rather than an error.
 */
async function ruleForRecord(
  siteId: string,
  record: AlarmApiRecord,
  skip: boolean
): Promise<AlarmRule | null> {
  const candidateRuleIds = [record.alarm_code].filter((id): id is string => Boolean(id));
  if (skip || candidateRuleIds.length === 0) return null;
  return resolveAlarmRule(siteId, candidateRuleIds);
}

async function sopsForRecord(record: AlarmApiRecord, rule: AlarmRule | null, logTag: string) {
  const sopIds = resolveSopIds(record, rule);
  const settled = await Promise.allSettled(sopIds.map(id => getArtifact(id)));
  const resolved: ResolvedSop[] = settled.map((res, i) => {
    const id = sopIds[i];
    if (res.status === 'fulfilled' && res.value) return artifactToResolvedSop(id, res.value);
    if (
      res.status === 'rejected' &&
      !(res.reason instanceof ApiError && res.reason.status === 404)
    ) {
      console.warn(`[${logTag}] SOP ${id} failed to load`, res.reason);
    }
    return { id, status: 'failed', steps: [] } as ResolvedSop;
  });
  return [
    ...resolved.filter(s => s.status === 'loaded'),
    ...resolved.filter(s => s.status === 'failed'),
  ];
}

/**
 * Fold a raw AEM record into the UI `Alarm`, with its rule and SOPs resolved.
 *
 * `skipRuleLookup` is for forced-mock mode, where there is no rule registry to
 * query — only the manual SOP fallback applies. `logTag` just labels the console
 * warning for a SOP that fails for a non-404 reason.
 */
export async function enrichRecordToAlarm(
  siteId: string,
  record: AlarmApiRecord,
  { skipRuleLookup = false, logTag = 'alarmEnrichment' } = {}
): Promise<Alarm> {
  // A registry error is not a missing rule: the manual SOP map still applies,
  // but the alarm is flagged so the page can say "severity unavailable" rather
  // than present the fallback as fact.
  let rule: AlarmRule | null = null;
  let ruleUnavailable = false;
  try {
    rule = await ruleForRecord(siteId, record, skipRuleLookup);
  } catch (err) {
    ruleUnavailable = true;
    console.warn(`[${logTag}] alarm rule lookup failed for ${record.alarm_code}`, err);
  }
  const sops = await sopsForRecord(record, rule, logTag);
  const alarm = alarmApiRecordToAlarm(
    record,
    sops,
    { resolveSeverity: () => priorityToSeverity(rule?.priority) },
    rule
  );
  return ruleUnavailable ? { ...alarm, ruleUnavailable } : alarm;
}
